// Send Windows W1 — shared helpers for the send-window editor + honesty surfaces.
// A window spec is { timezone, days:[1..7], ranges:[{start,end}] } where start/end
// are minutes-from-midnight (end exclusive). Kept framework-free so both the
// campaign + drip builders and their detail panels format windows identically.

const DAY_LABELS = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

/** minutes-from-midnight → "HH:MM" (1440 → "24:00"). */
export function minutesToHHMM(m) {
  const n = Math.max(0, Math.min(1440, Number(m) || 0));
  const h = Math.floor(n / 60);
  const mm = n % 60;
  return String(h).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
}

/** "HH:MM" → minutes-from-midnight. */
export function hhmmToMinutes(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
  if (!m) return 0;
  return Math.max(0, Math.min(1440, Number(m[1]) * 60 + Number(m[2])));
}

/** Sorted unique ISO days → "Mon–Fri, Sun" (collapses consecutive runs). */
export function formatDays(days) {
  const uniq = [...new Set((days || []).map(Number).filter((d) => d >= 1 && d <= 7))].sort((a, b) => a - b);
  if (uniq.length === 0) return '';
  if (uniq.length === 7) return 'Every day';
  const runs = [];
  let start = uniq[0];
  let prev = uniq[0];
  for (let i = 1; i < uniq.length; i++) {
    if (uniq[i] === prev + 1) { prev = uniq[i]; continue; }
    runs.push([start, prev]);
    start = uniq[i];
    prev = uniq[i];
  }
  runs.push([start, prev]);
  return runs
    .map(([a, b]) => (a === b ? DAY_LABELS[a] : (b === a + 1 ? `${DAY_LABELS[a]}, ${DAY_LABELS[b]}` : `${DAY_LABELS[a]}–${DAY_LABELS[b]}`)))
    .join(', ');
}

/** ranges → "10:00–11:00 and 18:00–22:00". */
export function formatRanges(ranges) {
  const parts = (ranges || [])
    .slice()
    .sort((a, b) => (a.start || 0) - (b.start || 0))
    .map((r) => `${minutesToHHMM(r.start)}–${minutesToHHMM(r.end)}`);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
}

/**
 * A one-line human summary, e.g. "Sends Mon–Fri, 10:00–11:00 and 18:00–22:00
 * (Europe/London)". Returns '' for an empty/absent spec.
 */
export function summariseWindow(spec) {
  if (!spec || !Array.isArray(spec.days) || !Array.isArray(spec.ranges) || !spec.days.length || !spec.ranges.length) {
    return '';
  }
  const days = formatDays(spec.days);
  const ranges = formatRanges(spec.ranges);
  const tz = spec.timezone ? ` (${spec.timezone})` : '';
  return `Sends ${days}, ${ranges}${tz}`;
}
